import { SkyButton, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { TenantResourceTargetsView } from "@shared/types/tenantResources";
import { useSetDefaultTarget } from "@/hooks/use-tenant-provisioning";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { targetOptionText } from "./TenantResourcesDrawer";

/**
 * "Default target for new tenants" (ADR 0010, design §6.4 and §7.5): a select of the registered
 * placement targets. Nobody types a subscription id. Only a holder of `platform.tenants.manage`
 * sees it; the server refuses an unregistered id with a 422, which is shown as its sentence.
 */
export const EXISTING_TENANTS_NEVER_MOVE = "Existing tenants never move.";

/** #906: said under the select while the only target is used with no default stored. */
export const ONLY_TARGET_IS_DEFAULT =
  "This is the only target, so new tenants are placed on it. Save it to keep it as the default when another target is added.";

/** #906: said under the select while the deployment's configured default is used, none stored. */
export const CONFIGURED_TARGET_IS_DEFAULT =
  "This is the default set for this deployment, so new tenants are placed on it. Save it to keep it as the default if the deployment's choice changes.";

/** #906: said while several targets are registered and none is the default. */
export const CHOOSE_DEFAULT_TARGET =
  "Choose a default, or pick a target in each new tenant's form.";

/**
 * The target a new tenant is placed on when its form picks none (#906): the stored default, else
 * the deployment's configured default (the target marked default in its environment file, else
 * the only registered target), else none. The server decides the same way
 * (`resolveDefaultTarget` in `shared/azure/tenantResourceTargets.ts`), so the screen never says
 * "No default target" while the server would place a tenant on one.
 */
export function effectiveDefaultTargetId(
  view: Pick<TenantResourceTargetsView, "defaultTargetId" | "configuredDefaultTargetId">,
): string | null {
  if (view.defaultTargetId) return view.defaultTargetId;
  return view.configuredDefaultTargetId;
}

export function DefaultTargetPicker({ view }: { view: TenantResourceTargetsView }) {
  const { can } = usePermissions();
  const save = useSetDefaultTarget();
  const notice = useNotice();
  const saveError = useInlineError();
  const [choice, setChoice] = useState<string | null>(null);
  if (!can("platform.tenants.manage")) return null;

  const effective = effectiveDefaultTargetId(view);
  const implied = view.defaultTargetId === null && effective !== null;
  const selected = choice ?? effective;
  // The only target, used with nothing stored, can be saved as the default (#906): it then stays
  // the default when a second target is registered.
  const changed = selected !== null && selected !== view.defaultTargetId;

  function submit() {
    if (selected === null) return;
    saveError.clear();
    save.mutate(selected, {
      onSuccess: () => {
        setChoice(null);
        notice.success("Default target saved");
      },
      onError: (error) => saveError.show(error, "The default target was not saved."),
    });
  }

  return (
    <section
      aria-label="Default target for new tenants"
      className="flex flex-col gap-2"
      data-testid="default-target-picker"
    >
      <InlineErrorBar
        error={saveError.error}
        title="The default target was not saved"
        onDismiss={saveError.clear}
        testId="default-target-error"
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-80">
          <SkyDropdown
            label="Default target for new tenants"
            options={view.targets.map((target) => ({
              value: target.id,
              text: targetOptionText(target),
            }))}
            selectedOptions={selected ? [selected] : []}
            onOptionSelect={(values) => setChoice(values[0] ?? null)}
            placeholder="No default target"
            testId="default-target-select"
          />
        </div>
        <SkyButton
          appearance="primary"
          onClick={submit}
          disabled={!changed}
          loading={save.isPending}
          testId="default-target-save"
        >
          Save default
        </SkyButton>
      </div>
      <SkyText size={200} color="muted" block testId="default-target-note">
        {implied && choice === null
          ? `${view.targets.length === 1 ? ONLY_TARGET_IS_DEFAULT : CONFIGURED_TARGET_IS_DEFAULT} `
          : effective === null
            ? `${CHOOSE_DEFAULT_TARGET} `
            : "A new tenant is placed on this target unless the form picks another. "}
        {EXISTING_TENANTS_NEVER_MOVE}
      </SkyText>
    </section>
  );
}
